import React, { useState, useEffect, useCallback, Suspense, lazy } from "react"
import { createPortal } from "react-dom"
import { RiaChatContext } from "../contexts/RiaChatContext"

function genId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID()
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

// RiaFloatingButton is small and needs to be visible immediately (it's the
// always-on-screen control), so it stays a synchronous require like before.
// RiaChatPanel is now React.lazy() instead: it previously loaded synchronously
// via require() on every page for every visitor, pulling its full dependency
// tree (useRiaChat -> riaApi -> riaFirebase's Firebase/Firestore webchannel
// code, plus MessageList's markdown renderer) into the main app bundle. A live
// PageSpeed "Reduce unused JavaScript" check measured ~129KB of this as never
// executed on a typical pageview. Only mounted below once the user actually
// opens the chat, so the code only downloads then.
let RiaFloatingButton = null

if (typeof window !== "undefined") {
  // Dynamic require avoids SSR issues with browser-only APIs inside this component
  RiaFloatingButton = require("../components/ria/RiaFloatingButton").default
}

const RiaChatPanel = lazy(() => import("../components/ria/RiaChatPanel"))

export default function RiaChatProvider({ children }) {
  const [isBrowser, setIsBrowser] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [hideRia, setHideRia] = useState(false)
  // Generate a session ID immediately — backend auto-creates the conversation
  // on first message. No API call needed before the user can type.
  const [conversationId, setConversationId] = useState(() => genId())
  const [prefill, setPrefill] = useState("")

  useEffect(() => {
    setIsBrowser(true)
  }, [])

  // ESC key closes the panel
  useEffect(() => {
    if (!isBrowser) return
    const handler = e => {
      if (e.key === "Escape") setIsOpen(false)
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [isBrowser])

  // Once true, stays true — keeps RiaChatPanel mounted (rather than
  // re-triggering its lazy import) across subsequent open/close cycles.
  const [hasOpenedRia, setHasOpenedRia] = useState(false)

  const openRia = useCallback((text = "") => {
    setPrefill(text)
    setIsOpen(true)
    setHasOpenedRia(true)
  }, [])

  const closeRia = useCallback(() => {
    setIsOpen(false)
  }, [])

  const contextValue = {
    isOpen,
    openRia,
    closeRia,
    conversationId,
    setConversationId,
    prefill,
    setPrefill,
    hideRia,
    setHideRia,
  }

  return (
    <RiaChatContext.Provider value={contextValue}>
      {children}
      {isBrowser && !hideRia && RiaFloatingButton && (
        <>
          {createPortal(
            <RiaFloatingButton />,
            document.body
          )}
          {hasOpenedRia &&
            createPortal(
              <Suspense fallback={null}>
                <RiaChatPanel />
              </Suspense>,
              document.body
            )}
        </>
      )}
    </RiaChatContext.Provider>
  )
}
